import React from "react";
import PropTypes from "prop-types";
import { Box, Paper, Typography } from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useShadowComparisonAccuracy } from "./ShadowBetaServices";

// Series colors validated with the dataviz palette checker (light surface):
// indigo/amber pass lightness, chroma, CVD separation (worst-pair dE 31.8),
// and 3:1 contrast. The mockup's gray CAP 1.0 fails the chroma floor, so the
// muted role is carried by the amber's lower visual weight instead.
const CAP2_COLOR = "#6366f1";
const CAP1_COLOR = "#b45309";
const GRID_COLOR = "#e2e8f0";

const StatTile = ({ label, value, sub }) => (
  <Paper variant="outlined" sx={{ px: 2.5, py: 1, textAlign: "center" }}>
    <Typography variant="h5" data-testid={`cap2-stat-${label}`}>
      {value ?? "—"}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {sub}
    </Typography>
  </Paper>
);

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  sub: PropTypes.string.isRequired,
};

const pct = (value) => (value == null ? null : `${value}%`);

/**
 * The mockup's analytics header: CAP 2.0 vs CAP 1.0 accuracy radar per
 * screening field, weekly accuracy trend, and current stat tiles - all
 * against the template's linked ground-truth dataset.
 */
export const AccuracyComparisonHeader = ({ templateId }) => {
  const { data } = useShadowComparisonAccuracy(templateId);
  if (!data) {
    return null;
  }
  const hasGroundTruth = (data.current?.n || 0) > 0;
  if (!hasGroundTruth) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2 }}
        data-testid="cap2-accuracy-empty"
      >
        <Typography variant="body2" color="text.secondary">
          No ground truth yet - accuracy charts appear once screened records
          have entries in this template&apos;s ground-truth dataset.
        </Typography>
      </Paper>
    );
  }
  const radarData = data.fields.map((field) => ({
    field: field.name,
    cap2: field.cap2_accuracy,
    cap1: field.cap1_accuracy,
    n: field.n,
  }));
  return (
    <Box
      sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}
      data-testid="cap2-accuracy-header"
    >
      <Paper variant="outlined" sx={{ p: 1.5, flex: "1 1 320px" }}>
        <Typography variant="overline" color="text.secondary">
          CAP 2.0 vs CAP 1.0 accuracy - per screening field
        </Typography>
        <ResponsiveContainer width="100%" height={210}>
          <RadarChart data={radarData} outerRadius="75%">
            <PolarGrid stroke={GRID_COLOR} />
            <PolarAngleAxis
              dataKey="field"
              tick={{ fontSize: 11, fill: "#64748b" }}
            />
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
            <Radar
              name="CAP 2.0"
              dataKey="cap2"
              stroke={CAP2_COLOR}
              strokeWidth={2}
              fill={CAP2_COLOR}
              fillOpacity={0.15}
            />
            <Radar
              name="CAP 1.0"
              dataKey="cap1"
              stroke={CAP1_COLOR}
              strokeWidth={2}
              fill={CAP1_COLOR}
              fillOpacity={0.1}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value, name, item) => [
                value == null
                  ? "—" // em dash: series has no measurement here (e.g. CAP 1.0 accuracy can be null)
                  : `${value}% (n=${item?.payload?.n ?? "?"})`,
                name,
              ]}
            />
          </RadarChart>
        </ResponsiveContainer>
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.5, flex: "2 1 420px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Overall model accuracy over time
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Weekly, against the linked ground-truth dataset
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <StatTile
              label="cap2"
              value={pct(data.current.cap2)}
              sub="CAP 2.0 (current)"
            />
            <StatTile
              label="cap1"
              value={pct(data.current.cap1)}
              sub="CAP 1.0 (current)"
            />
            <StatTile
              label="delta"
              value={
                data.current.delta == null
                  ? null
                  : `${data.current.delta > 0 ? "+" : ""}${data.current.delta} pts`
              }
              sub={`Δ overall (n=${data.current.n})`}
            />
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={150}>
          <LineChart
            data={data.weekly}
            margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis
              dataKey="week_start"
              tick={{ fontSize: 11, fill: "#64748b" }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              width={34}
              tick={{ fontSize: 11, fill: "#64748b" }}
              tickLine={false}
              axisLine={false}
            />
            <Line
              name="CAP 2.0"
              dataKey="cap2"
              stroke={CAP2_COLOR}
              strokeWidth={2}
              dot={{ r: 4 }}
              isAnimationActive={false}
            />
            <Line
              name="CAP 1.0"
              dataKey="cap1"
              stroke={CAP1_COLOR}
              strokeWidth={2}
              dot={{ r: 4 }}
              isAnimationActive={false}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Tooltip
              formatter={(value, name, item) => [
                value == null
                  ? "—" // em dash: series has no measurement here (e.g. CAP 1.0 accuracy can be null)
                  : `${value}% (n=${item?.payload?.n ?? "?"})`,
                name,
              ]}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>
    </Box>
  );
};

AccuracyComparisonHeader.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

export default AccuracyComparisonHeader;
